/**
 * Lógica pura de las estadísticas del WhatsApp oficial (§B.7): rangos de fechas, formatos,
 * el CSV de la serie diaria, las etiquetas de los grupos de uso de Twilio y las series que
 * alimentan los gráficos. Sin React, sin fetch, sin localStorage: todo se prueba en
 * `estadisticas.test.ts`.
 *
 * Regla de toda la sección: un dato que no se pudo medir se dice («—», «Sin mediciones»), no se
 * disfraza de 0. Y lo que informa Twilio se muestra como lo que es, un dato de Twilio: nunca cambia
 * lo que se le cobra a la marca (eso sale de la tabla de tarifas, fail-closed).
 */

import type {
  CostoConciliado, ErrorTwilio, FilaDia, FilaPlantilla, GrupoUso, HistorialSender, OrdenPlantillas, RangoFechas,
  AlertasTwilioConfig, EstadisticasTwilio, Tiempos, Percentiles, ResultadoSincronizacion, UsoMes,
  ConteoConsultas, ConteoEnviosBandeja, FilaDiaConsultas, OrigenConsulta,
} from "../types.estadisticas";
import { usd } from "./formato";
import { num, plural } from "../../campana-nueva/utils/format";

/**
 * Colores fijos de los gráficos (recharts pide hex, no clases de Tailwind). Son los de Tailwind que
 * ya usa el módulo: emerald = entrega, sky = lectura, violet = respuesta, red = fallo, amber =
 * advertencia, slate = neutro. «Enviados» va en violet-600, el color de acción del módulo, porque el
 * gris no se distingue de la grilla (validado con el script de paleta: las cuatro series de la
 * evolución pasan separación para daltonismo y contraste contra el fondo).
 */
export const COLORES = {
  enviados: "#7c3aed",     // violet-600
  entregados: "#059669",   // emerald-600
  leidos: "#0284c7",       // sky-600
  fallidos: "#dc2626",     // red-600
  respuestas: "#7c3aed",   // violet-600
  bajas: "#dc2626",        // red-600
  advertencia: "#d97706",  // amber-600
  neutro: "#64748b",       // slate-500
  grilla: "#e2e8f0",       // slate-200
  eje: "#94a3b8",          // slate-400
  costoPropio: "#0f172a",  // slate-900: la línea de «nuestra tabla» sobre las barras de Twilio
  saldo: "#059669",
  limite: "#0284c7",
} as const;

/** Colores de la calidad del número para las franjas de fondo (tintes claros: son fondo, no dato). */
export const COLOR_CALIDAD: Record<string, string> = {
  GREEN: "#d1fae5",   // emerald-100
  YELLOW: "#fef3c7",  // amber-100
  RED: "#fee2e2",     // red-100
  UNKNOWN: "#f1f5f9", // slate-100
};

/**
 * Grupos de Usage Records que se apilan en el gráfico de costos, en orden FIJO (el color sigue al
 * grupo, no a su posición: un día sin marketing no repinta los demás). Sin `total` (incluye voz y
 * otros productos) ni `gratis` (no tiene precio). Paleta validada: 7 categorías, el par
 * ámbar/esmeralda queda en la franja 6-8 para protanopia y por eso las barras llevan separador
 * blanco de 2 px, leyenda y la tabla del mes debajo.
 */
export const GRUPOS_COSTO: { grupo: Exclude<GrupoUso, "total" | "gratis">; color: string }[] = [
  { grupo: "meta_marketing", color: "#7c3aed" },     // violet-600
  { grupo: "meta_servicio", color: "#059669" },      // emerald-600
  { grupo: "twilio_saliente", color: "#d97706" },    // amber-600
  { grupo: "twilio_entrante", color: "#0284c7" },    // sky-600
  { grupo: "meta_utility", color: "#db2777" },       // pink-600
  { grupo: "meta_authentication", color: "#4f46e5" },// indigo-600
  { grupo: "engagement", color: "#a16207" },         // yellow-700
  { grupo: "otro", color: "#64748b" },               // slate-500 («otro» es resto, va en neutro)
];

const ETIQUETA_GRUPO: Record<GrupoUso, string> = {
  meta_marketing: "Plantillas de marketing (Meta)",
  meta_utility: "Plantillas de utilidad (Meta)",
  meta_authentication: "Plantillas de autenticación (Meta)",
  meta_servicio: "Mensajes de servicio (Meta)",
  gratis: "Conversaciones sin cargo de Meta",
  twilio_saliente: "Cargo de Twilio por mensaje enviado",
  twilio_entrante: "Cargo de Twilio por mensaje recibido",
  engagement: "Engagement Suite (acortador y clics)",
  total: "Total de la cuenta (todos los productos)",
  otro: "Otros cargos",
};

/** Etiqueta en castellano de un grupo de uso (§B.3.2). Un grupo desconocido se muestra crudo. */
export function etiquetaGrupoUso(grupo: GrupoUso | string): string {
  return (ETIQUETA_GRUPO as Record<string, string>)[grupo] ?? String(grupo || "Sin grupo");
}

/** Los grupos que forman «el gasto de WhatsApp según Twilio»: `meta_*`, `twilio_*` y `engagement`. Nunca `total`. */
export function esGrupoGastoWhatsapp(grupo: string): boolean {
  return grupo.startsWith("meta_") || grupo.startsWith("twilio_") || grupo === "engagement";
}

/**
 * Gasto del mes según Twilio (tarjeta del Resumen): suma de los grupos de WhatsApp de `usos.mes`.
 * null si ninguno trae precio: «Twilio todavía no informó» no es lo mismo que USD 0.
 */
export function gastoMesTwilio(mes: UsoMes[] | null | undefined): number | null {
  let hay = false;
  let suma = 0;
  for (const u of mes || []) {
    if (!esGrupoGastoWhatsapp(String(u?.grupo || ""))) continue;
    const p = Number(u?.price);
    if (u?.price === null || u?.price === undefined || !Number.isFinite(p)) continue;
    if (String(u.priceUnit || "USD").toUpperCase() !== "USD") continue;
    hay = true;
    suma += Math.abs(p);
  }
  return hay ? Math.round(suma * 10000) / 10000 : null;
}

// ── Fechas y rangos ──────────────────────────────────────────────────────────────────────────

/** La zona del negocio: la misma que usa el backend para agrupar por día (`TZ_NEGOCIO`). */
export const TZ_NEGOCIO = "America/Argentina/Buenos_Aires";
export const MAX_DIAS_RANGO = 400;
const RE_DIA = /^\d{4}-\d{2}-\d{2}$/;

/** Fecha → «YYYY-MM-DD» en la zona pedida (no en la del navegador: un operador de viaje ve los mismos días). */
export function diaEnZona(d: Date, tz = TZ_NEGOCIO): string {
  const partes = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const v = (t: string) => partes.find((p) => p.type === t)?.value ?? "";
  return `${v("year")}-${v("month")}-${v("day")}`;
}

/** true si es un día real con formato YYYY-MM-DD (un «2026-02-30» no pasa). */
export function esDiaValido(s: unknown): s is string {
  if (typeof s !== "string" || !RE_DIA.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/** Suma días a un «YYYY-MM-DD» con aritmética UTC (sin corrimientos por horario de verano). */
export function sumarDias(dia: string, n: number): string {
  const [y, m, d] = dia.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Días entre dos fechas, contando las dos puntas («2026-09-01» a «2026-09-01» = 1). */
export function diasEntre(desde: string, hasta: string): number {
  const a = Date.parse(`${desde}T00:00:00Z`);
  const b = Date.parse(`${hasta}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

export type ClaveRango = "7" | "30" | "90" | "mes" | "mes_pasado";
export const RANGOS: { clave: ClaveRango; label: string }[] = [
  { clave: "7", label: "7 días" },
  { clave: "30", label: "30 días" },
  { clave: "90", label: "90 días" },
  { clave: "mes", label: "Este mes" },
  { clave: "mes_pasado", label: "Mes pasado" },
];
export const RANGO_POR_DEFECTO: ClaveRango = "30";

/**
 * Rango de un botón del selector, con «hoy» en la zona del negocio. «7 días» son hoy y los 6
 * anteriores (7 días contando hoy, como el default de 30 del backend). «Mes pasado» en septiembre
 * da 01/08 a 31/08.
 */
export function rangoPredefinido(clave: ClaveRango, ahora: Date, tz = TZ_NEGOCIO): RangoFechas {
  const hoy = diaEnZona(ahora, tz);
  switch (clave) {
    case "7": return { desde: sumarDias(hoy, -6), hasta: hoy };
    case "90": return { desde: sumarDias(hoy, -89), hasta: hoy };
    case "mes": return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
    case "mes_pasado": {
      const primeroEste = `${hoy.slice(0, 7)}-01`;
      const ultimoPasado = sumarDias(primeroEste, -1);
      return { desde: `${ultimoPasado.slice(0, 7)}-01`, hasta: ultimoPasado };
    }
    case "30":
    default:
      return { desde: sumarDias(hoy, -29), hasta: hoy };
  }
}

/** Error en castellano de un rango personalizado, o null si sirve (mismas reglas que el backend). */
export function validarRango(desde: string, hasta: string): string | null {
  if (!esDiaValido(desde) || !esDiaValido(hasta)) return "Elegí las dos fechas.";
  if (hasta < desde) return "La fecha «hasta» tiene que ser igual o posterior a «desde».";
  if (diasEntre(desde, hasta) > MAX_DIAS_RANGO) return `El rango puede tener hasta ${MAX_DIAS_RANGO} días.`;
  return null;
}

/**
 * El rango vive en la URL (`?rango=7|30|90|mes|mes_pasado` o `?desde=&hasta=`), así un enlace
 * copiado abre lo mismo. Una URL con fechas malas no rompe la página: cae al default y lo dice.
 */
export function rangoDesdeParams(p: URLSearchParams, ahora: Date, tz = TZ_NEGOCIO):
  { rango: RangoFechas; clave: ClaveRango | "personalizado"; error: string | null } {
  const desde = p.get("desde");
  const hasta = p.get("hasta");
  if (desde !== null || hasta !== null) {
    const error = validarRango(desde || "", hasta || "");
    if (!error) return { rango: { desde: desde as string, hasta: hasta as string }, clave: "personalizado", error: null };
    return { rango: rangoPredefinido(RANGO_POR_DEFECTO, ahora, tz), clave: RANGO_POR_DEFECTO, error: `Las fechas de la dirección no sirven (${error}) Mostramos los últimos 30 días.` };
  }
  const r = p.get("rango");
  const clave = RANGOS.some((x) => x.clave === r) ? (r as ClaveRango) : RANGO_POR_DEFECTO;
  return { rango: rangoPredefinido(clave, ahora, tz), clave, error: null };
}

/** «2026-09-23» → «23/09». Lo que no es un día se devuelve tal cual. */
export function ddmm(dia: string): string {
  return esDiaValido(dia) ? `${dia.slice(8, 10)}/${dia.slice(5, 7)}` : String(dia || "");
}

/**
 * Antigüedad en texto: «hace 40 s», «hace 3 min», «hace 2 h», «hace 1 día». null o inválido →
 * null (quien llama decide cómo decir «nunca»). Una fecha futura (reloj corrido) se lee «recién».
 */
export function haceTexto(at: string | null | undefined, ahora: Date): string | null {
  if (!at) return null;
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return null;
  const seg = Math.floor((ahora.getTime() - t) / 1000);
  if (seg < 5) return "recién";
  if (seg < 60) return `hace ${seg} s`;
  const min = Math.floor(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} ${d === 1 ? "día" : "días"}`;
}

/**
 * «Datos de Twilio actualizados hace X»: manda el MÁS VIEJO de los `sync.*At`, porque es el que
 * limita qué tan fresco está todo. Si alguno nunca corrió, se dice (no se esconde detrás del resto).
 */
export function textoSync(sync: EstadisticasTwilio["sync"] | null | undefined, ahora: Date): string {
  if (!sync) return "Todavía no trajimos datos de Twilio.";
  const fechas = [sync.saldoAt, sync.usosAt, sync.preciosAt, sync.senderAt, sync.triggersAt];
  const validas = fechas.filter((f): f is string => !!f && Number.isFinite(Date.parse(f)));
  if (!validas.length) return "Todavía no trajimos datos de Twilio.";
  const masViejo = validas.reduce((a, b) => (Date.parse(a) <= Date.parse(b) ? a : b));
  const base = `Datos de Twilio actualizados ${haceTexto(masViejo, ahora)}`;
  return validas.length < fechas.length ? `${base} (hay datos que todavía no se trajeron nunca)` : base;
}

// ── Formatos ─────────────────────────────────────────────────────────────────────────────────

/** Duración en texto: «12 s», «3 min», «2 h 10 min», «1 d 4 h». null, negativo o no finito → «—». */
export function duracionTexto(seg: number | null | undefined): string {
  if (seg === null || seg === undefined || !Number.isFinite(Number(seg)) || Number(seg) < 0) return "—";
  const s = Math.round(Number(seg));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86_400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  return h ? `${d} d ${h} h` : `${d} d`;
}

/** Una tasa que llega como fracción (0,9517) → «95,2 %». No finita → «—». */
export function tasaTexto(t: number | null | undefined): string {
  if (t === null || t === undefined || !Number.isFinite(Number(t))) return "—";
  return `${(Math.round(Number(t) * 1000) / 10).toLocaleString("es-AR")} %`;
}

/** Un porcentaje ya calculado (12,345) → «+12,3 %» con signo. null → null. */
export function pctConSigno(p: number | null | undefined): string | null {
  if (p === null || p === undefined || !Number.isFinite(Number(p))) return null;
  const n = Math.round(Number(p) * 10) / 10;
  return `${n > 0 ? "+" : ""}${n.toLocaleString("es-AR")} %`;
}

// ── Sobre qué base va cada tasa ──────────────────────────────────────────────────────────────

/**
 * La base de cada tasa (las de `tasas()` del backend): entrega y bajas sobre los ENVIADOS (una baja puede ser de alguien
 * a quien Meta no le entregó, el 131050); lectura y respuesta sobre los ENTREGADOS. El embudo de la campaña cuenta todo
 * sobre los enviados y las tasas no: sin decirlo al lado de cada porcentaje, el «% de leídos» de una pantalla no
 * cerraba con el de otra y el de bajas parecía calculado sobre otra cosa.
 */
export const BASE_TASA = { entrega: "enviados", lectura: "entregados", respuesta: "entregados", bajas: "enviados" } as const;
export const NOTA_BASES_TASAS = "Entrega y bajas, sobre los enviados; lectura y respuesta, sobre los entregados.";

/** «700 de 1.140 entregados»: el numerador de una tasa y su base, con la base en singular si es 1. */
export function sobreLaBase(parte: number | null | undefined, base: number | null | undefined, cual: "enviados" | "entregados"): string {
  const b = Number(base) || 0;
  const nombre = cual === "enviados" ? plural(b, "enviado", "enviados") : plural(b, "entregado", "entregados");
  return `${num(Number(parte) || 0)} de ${num(b)} ${nombre}`;
}

// ── Textos fijos (§6.5) ──────────────────────────────────────────────────────────────────────

export const LEIDO_ACLARACION = "\"Leído\" sólo se mide si la persona tiene activadas las confirmaciones de lectura.";
export const ACLARACION_COSTO_TWILIO = "Es el precio que Twilio informa en cada mensaje, que puede tardar en aparecer. La documentación de Twilio no aclara si incluye la tarifa de Meta; comparalo con el uso por categoría de abajo. Lo que se descuenta del saldo de la marca sigue saliendo de nuestra tabla de tarifas.";
// Las dos de los clics salen en Estadísticas y en el detalle de una campaña, que lee el comercio: «el proveedor», no «Twilio».
export const ACLARACION_CLICS = "Los clics pueden incluir las vistas previas que arma el teléfono al recibir un link: el proveedor de WhatsApp no las separa en este aviso.";
export const ACLARACION_LINKS = "El proveedor de WhatsApp acorta y mide los links de los mensajes de servicio. En los botones de las plantillas aprobadas (las campañas) no está asegurado que se midan los clics.";
export const ACLARACION_ERRORES_TWILIO = "Lo que Twilio registró en tu cuenta en los últimos 30 días (Twilio no guarda más). \"Al avisarle a Deenex\" son las veces que Twilio no pudo entregarnos un aviso.";
export const ACLARACION_SUBCUENTA = "Esta cuenta es una subcuenta: Twilio no informa su saldo. Miralo en la consola de la cuenta principal.";
export const ACLARACION_SALDO_TWILIO = "Es la plata que Twilio le cobra a Deenex. El saldo de abajo es el interno de la marca, que se descuenta en cada envío.";
export const ACLARACION_UTC = "Twilio cuenta el día y el mes en horario UTC.";
export const TEXTO_FALLA_A_DEENEX = "Twilio no pudo avisarle a Deenex: revisá la conexión en Configuración.";
export const TEXTO_SIN_ENVIOS = "Todavía no hay envíos en estas fechas";
export const TEXTO_CONECTAR = "Conectá la cuenta de Twilio para ver estos datos";
export const CONSOLA_TWILIO = "https://console.twilio.com/";

/** `motivoNoDisponible` con las credenciales reales andando y sin número: lo del número no está, lo de la cuenta sí. */
export const MOTIVO_SIN_NUMERO = "sin_numero";
export const TEXTO_SIN_NUMERO = "La cuenta está conectada; falta el número de WhatsApp";

/**
 * ¿El motivo es «falta el número»? Llega como código (`sin_numero`) o como el texto del backend
 * («La cuenta de Twilio está conectada; falta el número de WhatsApp»): con cualquiera de los dos, la
 * pantalla no puede pedir «Conectá la cuenta» con la cuenta ya conectada.
 */
export function esSinNumero(motivo: string | null | undefined): boolean {
  const m = String(motivo || "").trim();
  return m === MOTIVO_SIN_NUMERO || /falta el n[uú]mero/i.test(m);
}

/** El título del aviso de una tarjeta de Twilio sin datos: «falta el número» o «conectá la cuenta». */
export function textoAvisoConectar(motivo: string | null | undefined): string {
  return esSinNumero(motivo) ? TEXTO_SIN_NUMERO : TEXTO_CONECTAR;
}

// El aviso de «Actualizar datos» sale en el encabezado de Estadísticas, que lee el comercio: sin «Twilio».
export const TEXTO_SYNC_OK = "Listo: trajimos los datos actualizados.";
const NOMBRE_TAREA_SYNC: Record<string, string> = {
  saldo: "el saldo", usos: "los usos", precios: "los precios", sender: "el estado del número", triggers: "las alertas de gasto",
};

/** «a», «a y b», «a, b y c». */
function enumerar(xs: string[]): string {
  return xs.length > 1 ? `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}` : xs[0] || "";
}

export type AvisoSincronizacion = { tono: "ok" | "neutro" | "error"; texto: string };

/**
 * Qué decir después de «Actualizar datos de Twilio». Con la cuenta conectada y sin número, el backend
 * devuelve {ok:false} en lo del número con el texto de `esSinNumero`: es el estado de la cuenta, no una
 * falla de Twilio, así que va como aviso neutro que dice qué falta. Una falla de verdad sigue siendo un
 * error (y lo del número, si también vino, se aclara aparte).
 */
export function avisoSincronizacion(r: ResultadoSincronizacion | null | undefined): AvisoSincronizacion {
  const corridas = r?.corridas || [];
  const fallas = corridas.filter((c) => !c.ok);
  const sinNumero = fallas.filter((c) => esSinNumero(c.detalle));
  const otras = fallas.filter((c) => !esSinNumero(c.detalle));
  const nombres = sinNumero.map((c) => NOMBRE_TAREA_SYNC[c.tarea] || c.tarea);
  const plural = nombres.length > 1 || /^(los|las) /.test(nombres[0] || "");
  const falta = nombres.length ? `Falta el número de WhatsApp: ${enumerar(nombres)} se ${plural ? "traen" : "trae"} cuando lo registres.` : "";
  if (otras.length) {
    const error = `No se pudo actualizar: ${otras.map((f) => `${f.tarea}${f.detalle ? ` (${f.detalle})` : ""}`).join(", ")}.`;
    return { tono: "error", texto: falta ? `${error} ${falta}` : error };
  }
  if (falta) return { tono: "neutro", texto: corridas.some((c) => c.ok) ? `Trajimos lo de la cuenta. ${falta}` : falta };
  return { tono: "ok", texto: TEXTO_SYNC_OK };
}

/** «Uso por categoría» con la fecha de corte de Twilio (`as_of`). */
export function aclaracionUso(asOf: string | null | undefined): string {
  const f = asOf && Number.isFinite(Date.parse(asOf))
    ? new Date(asOf).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "(sin fecha de corte)";
  return `Datos de facturación de Twilio (Usage Records), actualizados al ${f}. El cargo de Twilio por mensaje vale para cualquier canal: si la cuenta también manda SMS, están incluidos.`;
}

/** Los cinco tramos de «Tiempos», en el orden en que se muestran. */
export const TRAMOS: { clave: keyof Omit<Tiempos, "muestreado" | "aclaracion">; titulo: string }[] = [
  { clave: "llamadaAEnviado", titulo: "De Deenex a enviado" },
  { clave: "enviadoAEntregado", titulo: "De enviado a entregado" },
  { clave: "entregadoALeido", titulo: "De entregado a leído" },
  { clave: "llamadaAEntregado", titulo: "De Deenex a entregado" },
  { clave: "entregadoARespuesta", titulo: "De entregado a la primera respuesta" },
];

/** Suma de los tramos: cuántas mediciones hubo y cuántas con la hora de Twilio en las dos puntas. */
export function conteoHoraTwilio(t: Tiempos | null | undefined): { n: number; total: number } {
  let n = 0;
  let total = 0;
  for (const { clave } of TRAMOS) {
    const p = t?.[clave] as Percentiles | undefined;
    total += Math.max(0, Number(p?.n) || 0);
    n += Math.max(0, Number(p?.conHoraTwilio) || 0);
  }
  return { n: Math.min(n, total), total };
}

/**
 * La aclaración de los tiempos para una persona de un cliente (Accesos, rol «comercio»). La del equipo (la del backend o
 * `aclaracionTiempos`) habla de Twilio y de Event Streams, que el comercio no sabe qué son: la tarjeta de los tiempos
 * sale en Estadísticas y en el detalle de su campaña.
 */
export const ACLARACION_TIEMPOS_COMERCIO =
  "Los tiempos van desde que Deenex mandó cada mensaje a WhatsApp. Cuando WhatsApp informa la hora exacta de cada paso, se usa esa; si no, la hora en que nos llegó el aviso.";

/**
 * Aclaración fija de los tiempos (§6.5). RESPALDO: la pantalla muestra `tiempos.aclaracion` del
 * backend, que cuenta mensajes; esto cuenta mediciones por tramo y sólo se usa si esa falta.
 */
export function aclaracionTiempos(t: Tiempos | null | undefined): string {
  const { n, total } = conteoHoraTwilio(t);
  return `Los tiempos van desde que Deenex le pasó el mensaje a Twilio. Cuando Twilio informa la hora exacta de cada estado (Event Streams), se usa esa; si no, la hora en que nos llegó el aviso. ${n.toLocaleString("es-AR")} de ${total.toLocaleString("es-AR")} se midieron con la hora de Twilio.`;
}

/**
 * Resumen de la conciliación del precio de Twilio contra nuestra tabla, en una frase. Sin precio
 * todavía, se dice eso (no «USD 0»); con estimado 0 no hay porcentaje; con otras monedas, se avisa
 * que la suma sólo cuenta dólares.
 */
export function textoConciliacion(c: CostoConciliado | null | undefined): string {
  if (!c || !(Number(c.mensajesTotales) > 0)) return "Todavía no hay mensajes para comparar.";
  if (!(Number(c.mensajesConPrecio) > 0)) return "Twilio todavía no informó el precio de ningún mensaje: puede tardar unas horas.";
  const cobertura = `Twilio informó el precio de ${c.mensajesConPrecio.toLocaleString("es-AR")} de ${c.mensajesTotales.toLocaleString("es-AR")} ${plural(c.mensajesTotales, "mensaje", "mensajes")} (${(Math.round((Number(c.coberturaPct) || 0) * 10) / 10).toLocaleString("es-AR")} %).`;
  const dif = pctConSigno(c.diferenciaPct);
  const comparacion = `Suman ${usd(c.precioTwilioUSD, 4)} contra ${usd(c.estimadoTablaUSD, 4)} de nuestra tabla${dif ? ` (${dif})` : " (sin estimado para comparar)"}.`;
  const otras = (c.unidadesDistintas || []).filter((u) => u && u.toUpperCase() !== "USD");
  const monedas = otras.length ? ` Hay precios en ${otras.join(", ")}: la suma sólo cuenta los que vienen en USD.` : "";
  return `${cobertura} ${comparacion}${monedas}`;
}

// ── CSV ──────────────────────────────────────────────────────────────────────────────────────

/**
 * `soloEquipo`: lo que le cuesta a Deenex (el costo propio) y lo que informa Twilio. El CSV lo baja también una persona
 * de un cliente (Accesos, rol «comercio») desde el encabezado de Estadísticas: a ella le llegaban esas columnas (hoy el
 * backend ni le manda los datos), y su CSV va sin ellas.
 */
const COLUMNAS_CSV: { titulo: string; valor: (f: FilaDia) => number | string | null; soloEquipo?: true }[] = [
  { titulo: "Fecha", valor: (f) => f.fecha },
  { titulo: "Enviados", valor: (f) => f.enviados },
  { titulo: "Entregados", valor: (f) => f.entregados },
  { titulo: "Leídos", valor: (f) => f.leidos },
  { titulo: "Fallidos", valor: (f) => f.fallidos },
  { titulo: "No entregados", valor: (f) => f.noEntregados },
  { titulo: "Sin confirmación", valor: (f) => f.sinConfirmacion },
  { titulo: "Respuestas", valor: (f) => f.respuestas },
  { titulo: "Bajas", valor: (f) => f.bajas },
  { titulo: "Costo propio (USD)", valor: (f) => f.costoPropioUSD ?? null, soloEquipo: true },
  { titulo: "Cobrado a la marca (USD)", valor: (f) => f.cobradoClienteUSD },
  // El CSV lo baja el comercio desde el encabezado de Estadísticas: «el proveedor», no «Twilio».
  { titulo: "Precio según el proveedor (USD)", valor: (f) => f.precioTwilioUSD ?? null, soloEquipo: true },
  { titulo: "Uso según el proveedor (USD)", valor: (f) => f.usoTwilioUSD ?? null, soloEquipo: true },
];

/** Número para Excel en castellano: coma decimal, sin notación científica, hasta 6 decimales. Vacío si no hay dato. */
function numeroCsv(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "";
  const s = Number(n).toFixed(6).replace(/\.?0+$/, "");
  return (s === "-0" ? "0" : s).replace(".", ",");
}

function celdaCsv(v: number | string | null): string {
  if (typeof v === "number" || v === null) return numeroCsv(v);
  const s = String(v ?? "");
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Serie diaria → CSV para Excel en castellano: BOM UTF-8 (sin él Excel abre «LeÃ­dos»), `;` de
 * separador y coma decimal (con `,` y punto, Excel es-AR mete todo en una columna o lee 0.0075
 * como 75). Un dato ausente (`usoTwilioUSD` null) queda vacío, no 0. Con `equipo: false` (una persona de un cliente),
 * sin las columnas `soloEquipo`.
 */
export function serieACsv(serie: FilaDia[] | null | undefined, { equipo = true }: { equipo?: boolean } = {}): string {
  const columnas = equipo ? COLUMNAS_CSV : COLUMNAS_CSV.filter((c) => !c.soloEquipo);
  const filas = [columnas.map((c) => c.titulo).join(";")];
  for (const f of serie || []) filas.push(columnas.map((c) => celdaCsv(c.valor(f))).join(";"));
  return `\uFEFF${filas.join("\r\n")}\r\n`;
}

export function nombreCsv(r: RangoFechas): string {
  return `estadisticas-whatsapp-${r.desde}-a-${r.hasta}.csv`;
}

// ── Series para los gráficos ─────────────────────────────────────────────────────────────────

export type FilaCosto = { fecha: string; etiqueta: string; costoPropioUSD: number | null } & Partial<Record<GrupoUso, number>>;

/**
 * Barras apiladas de Usage Records por día + la línea de nuestro costo (`costoPropioUSD` de la
 * serie de la marca). Un día que está en una serie y no en la otra aparece igual. `total` y
 * `gratis` no se apilan; un grupo desconocido suma en `otro`.
 */
export function serieCostos(
  usos: EstadisticasTwilio["usos"]["serie"] | null | undefined,
  marca: FilaDia[] | null | undefined,
): FilaCosto[] {
  const porDia = new Map<string, FilaCosto>();
  const fila = (fecha: string) => {
    let f = porDia.get(fecha);
    if (!f) { f = { fecha, etiqueta: ddmm(fecha), costoPropioUSD: null }; porDia.set(fecha, f); }
    return f;
  };
  const validos = new Set<string>(GRUPOS_COSTO.map((g) => g.grupo));
  for (const u of usos || []) {
    if (!u?.fecha) continue;
    const f = fila(u.fecha);
    for (const [g, v] of Object.entries(u.porGrupo || {})) {
      const n = Math.abs(Number(v));
      if (!Number.isFinite(n) || g === "total" || g === "gratis") continue;
      const clave = (validos.has(g) ? g : "otro") as GrupoUso;
      f[clave] = Math.round(((f[clave] ?? 0) + n) * 10000) / 10000;
    }
  }
  for (const m of marca || []) {
    if (!m?.fecha) continue;
    const c = Number(m.costoPropioUSD);
    fila(m.fecha).costoPropioUSD = Number.isFinite(c) ? c : null;
  }
  return [...porDia.values()].sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
}

/** Los grupos que aparecen con algún valor en la serie (la leyenda no lista grupos vacíos). */
export function gruposPresentes(serie: FilaCosto[]): typeof GRUPOS_COSTO {
  return GRUPOS_COSTO.filter((g) => serie.some((f) => (Number(f[g.grupo]) || 0) > 0));
}

/**
 * Límite del número en el tiempo, para una línea escalonada. «Sin límite» no tiene número: se
 * dibuja en una punta superior (el mayor límite visto × 1,5, o 100.000) con la etiqueta aparte.
 * Un límite desconocido (null sin ilimitado) corta la línea: no se inventa un escalón.
 */
export function serieLimite(historial: HistorialSender[] | null | undefined):
  { puntos: { t: number; limite: number | null; ilimitado: boolean }[]; techo: number } {
  const validos = (historial || []).filter((h) => h && Number.isFinite(Date.parse(h.at)));
  const finitos = validos.map((h) => Number(h.limite)).filter((n) => Number.isFinite(n) && n > 0);
  const techo = finitos.length ? Math.ceil(Math.max(...finitos) * 1.5) : 100_000;
  const puntos = validos
    .map((h) => ({
      t: Date.parse(h.at),
      ilimitado: h.ilimitado === true,
      limite: h.ilimitado === true ? techo : Number.isFinite(Number(h.limite)) && h.limite !== null ? Number(h.limite) : null,
    }))
    .sort((a, b) => a.t - b.t);
  return { puntos, techo };
}

/**
 * Tramos de calidad para las franjas de fondo: cada snapshot abre un tramo que dura hasta el
 * siguiente (el último, hasta `ahora`). Tramos seguidos con la misma calidad se juntan.
 */
export function tramosCalidad(historial: HistorialSender[] | null | undefined, ahora: Date):
  { desde: number; hasta: number; calidad: string; color: string }[] {
  const hs = (historial || [])
    .filter((h) => h && Number.isFinite(Date.parse(h.at)))
    .map((h) => ({ t: Date.parse(h.at), calidad: normalizarCalidad(h.calidad) }))
    .sort((a, b) => a.t - b.t);
  const tramos: { desde: number; hasta: number; calidad: string; color: string }[] = [];
  hs.forEach((h, i) => {
    const hasta = i + 1 < hs.length ? hs[i + 1].t : Math.max(h.t, ahora.getTime());
    const previo = tramos[tramos.length - 1];
    if (previo && previo.calidad === h.calidad) previo.hasta = hasta;
    else tramos.push({ desde: h.t, hasta, calidad: h.calidad, color: COLOR_CALIDAD[h.calidad] });
  });
  return tramos;
}

/** HIGH/MEDIUM/LOW (Senders v2) o GREEN/YELLOW/RED → GREEN/YELLOW/RED; lo demás, UNKNOWN. */
export function normalizarCalidad(raw: unknown): "GREEN" | "YELLOW" | "RED" | "UNKNOWN" {
  const s = String(raw ?? "").trim().toUpperCase();
  if (s === "HIGH" || s === "GREEN") return "GREEN";
  if (s === "MEDIUM" || s === "YELLOW") return "YELLOW";
  if (s === "LOW" || s === "RED") return "RED";
  return "UNKNOWN";
}

/** true si algún código de Twilio falló al avisarle a Deenex (franja ámbar de la tarjeta 12). */
export function hayFallasADeenex(errores: ErrorTwilio[] | null | undefined): boolean {
  return (errores || []).some((e) => Number(e?.aDeenex) > 0);
}

/** Orden de la tabla de plantillas (descendente; a igualdad, más enviados primero y después por nombre). */
export function ordenarPlantillas(filas: FilaPlantilla[] | null | undefined, orden: OrdenPlantillas): FilaPlantilla[] {
  const valor = (f: FilaPlantilla) => (orden === "enviados" ? Number(f.enviados) || 0 : Number(f.tasas?.[orden]) || 0);
  return [...(filas || [])].sort((a, b) =>
    valor(b) - valor(a) || (Number(b.enviados) || 0) - (Number(a.enviados) || 0) || String(a.nombreVisible).localeCompare(String(b.nombreVisible), "es"));
}

// ── Alertas de gasto ─────────────────────────────────────────────────────────────────────────

export const MAX_MONTO_ALERTA = 1_000_000;
export const ALERTAS_POR_DEFECTO: AlertasTwilioConfig = { gastoDiarioUSD: null, gastoMensualUSD: null, saldoMinimoUSD: 10 };

/** Lee un monto escrito con coma o punto («12,5» o «12.5»). Vacío → null; basura → NaN. */
export function leerMonto(s: string): number | null {
  const t = String(s ?? "").trim();
  if (!t) return null;
  if (!/^\d+([.,]\d+)?$/.test(t)) return NaN;
  return Number(t.replace(",", "."));
}

/**
 * Formulario de alertas de gasto (Configuración → Conexión (técnico)). Vacío = sin alerta. Mismas
 * reglas que el backend (§B.6.2): gasto diario y mensual null o en (0, 1.000.000]; saldo mínimo en
 * [0, 1.000.000] (vacío = 0, que nunca dispara). Se valida acá para no mandar algo que va a volver
 * con 400.
 */
export function validarAlertasGasto(f: { gastoDiario: string; gastoMensual: string; saldoMinimo: string }):
  { ok: true; config: AlertasTwilioConfig } | { ok: false; errores: Partial<Record<"gastoDiario" | "gastoMensual" | "saldoMinimo", string>> } {
  const errores: Partial<Record<"gastoDiario" | "gastoMensual" | "saldoMinimo", string>> = {};
  const gasto = (clave: "gastoDiario" | "gastoMensual"): number | null => {
    const n = leerMonto(f[clave]);
    if (n === null) return null;
    if (!Number.isFinite(n) || n <= 0 || n > MAX_MONTO_ALERTA) errores[clave] = "Tiene que ser un monto mayor a 0 y hasta USD 1.000.000, o vacío.";
    return n;
  };
  const gastoDiarioUSD = gasto("gastoDiario");
  const gastoMensualUSD = gasto("gastoMensual");
  const saldo = leerMonto(f.saldoMinimo);
  if (saldo !== null && (!Number.isFinite(saldo) || saldo < 0 || saldo > MAX_MONTO_ALERTA)) errores.saldoMinimo = "Tiene que ser un monto entre 0 y USD 1.000.000, o vacío.";
  if (Object.keys(errores).length) return { ok: false, errores };
  return { ok: true, config: { gastoDiarioUSD, gastoMensualUSD, saldoMinimoUSD: saldo ?? 0 } };
}

/** Config de alertas → valores del formulario (null → vacío). */
export function formularioAlertas(c: AlertasTwilioConfig | null | undefined): { gastoDiario: string; gastoMensual: string; saldoMinimo: string } {
  const a = c ?? ALERTAS_POR_DEFECTO;
  const s = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(Number(n)) ? "" : String(n).replace(".", ","));
  return { gastoDiario: s(a.gastoDiarioUSD), gastoMensual: s(a.gastoMensualUSD), saldoMinimo: a.saldoMinimoUSD ? s(a.saldoMinimoUSD) : "" };
}

// ── Conversaciones ───────────────────────────────────────────────────────────────────────────
// La atención de la bandeja (`GET /estadisticas/conversaciones`). La unidad es la CONSULTA: lo que la
// bandeja llama «espera», cada vez que alguien escribe y queda esperando respuesta.

export const TEXTO_SIN_CONSULTAS = "Nadie escribió en estas fechas.";
/** La marca todavía no recibió ningún mensaje desde que existe la medición (`medidoDesde` null). */
export const TEXTO_SIN_MEDIR = "Todavía no se midió ninguna consulta: se cuentan desde el primer mensaje que llegue.";
export const ACLARACION_CONSULTAS = "Una consulta es cada vez que alguien escribe y queda esperando respuesta: lo que manda seguido, antes de que le contesten, es una sola. Si pasan 24 h desde su último mensaje sin respuesta y vuelve a escribir, es una consulta nueva. «A tiempo» cuenta las respondidas con texto sobre las que esperaban respuesta: no suma las cerradas sin responder, las bajas ni las que siguen esperando. Si una consulta no necesita respuesta (un «gracias»), marcala resuelta antes de que pasen las 24 h: después sigue contando como vencida.";
export const ACLARACION_PRIMERA_RESPUESTA = "Desde el primer mensaje de la consulta hasta que alguien tocó Enviar en la respuesta con texto. Cuenta las noches y los fines de semana. No entran las reaperturas con plantilla ni las respuestas automáticas.";

/**
 * Colores de las consultas. El gráfico por día apila cuatro series y cada fila del desglose lleva el
 * color de la serie en la que cae: un color dice lo mismo en toda la sección. Validado con el script de
 * paleta en este orden de apilado: con «Cerradas sin responder» en gris no pasaba el piso de croma (se
 * confunde con la grilla), y con el rojo pegado al ámbar no se distinguían ni con visión normal (ΔE 14,4).
 * Las dos series de abajo son la base de «Respondidas a tiempo».
 */
export const COLOR_CONSULTA = {
  respondidas: COLORES.respuestas,  // violet-600
  sinRespuesta: COLORES.fallidos,   // red-600
  cerradas: COLORES.leidos,         // sky-600
  esperando: COLORES.advertencia,   // amber-600
} as const;

export type ClaveDesglose = "respondidas" | "reabiertas" | "resueltas" | "bajas" | "vencidas" | "enCurso";
/** «Qué pasó con cada consulta», en el orden en que se muestra. Las seis suman el total. */
export const DESGLOSE_CONSULTAS: { clave: ClaveDesglose; label: string; detalle: string; color: string }[] = [
  { clave: "respondidas", label: "Respondidas con texto", detalle: "Dentro de la ventana de 24 h de WhatsApp (se cuenta desde su último mensaje)", color: COLOR_CONSULTA.respondidas },
  { clave: "reabiertas", label: "Reabiertas con plantilla", detalle: "Con la ventana cerrada: la plantilla se paga aparte", color: COLOR_CONSULTA.sinRespuesta },
  // «Alguien del equipo» no se entendía del lado del cliente (¿su equipo o el de Deenex?): lo hace cualquiera de los dos.
  { clave: "resueltas", label: "Cerradas sin responder", detalle: "Alguien la marcó resuelta con la ventana abierta (por ejemplo, un «gracias»)", color: COLOR_CONSULTA.cerradas },
  { clave: "bajas", label: "Pidieron la baja", detalle: "Por mensaje, o alguien la registró a mano, con la ventana abierta", color: COLOR_CONSULTA.cerradas },
  { clave: "vencidas", label: "Vencieron sin respuesta", detalle: "Pasaron 24 h desde su último mensaje sin que nadie contestara ni la cerrara", color: COLOR_CONSULTA.sinRespuesta },
  { clave: "enCurso", label: "Esperando ahora", detalle: "Todavía se pueden contestar con texto", color: COLOR_CONSULTA.esperando },
];

/** «De dónde vinieron», en el orden de la tabla. */
export const ORIGENES_CONSULTA_TEXTO: { origen: OrigenConsulta; label: string; detalle: string }[] = [
  { origen: "campana", label: "Por una campaña", detalle: "Citaron un mensaje de campaña o les llegó una en los 7 días anteriores" },
  { origen: "anuncio", label: "Por un anuncio", detalle: "Tocaron un anuncio de Click-to-WhatsApp" },
  { origen: "seguimiento", label: "Seguían una charla", detalle: "Ya les habían escrito desde Conversaciones en los 7 días anteriores" },
  { origen: "espontanea", label: "Por su cuenta", detalle: "Sin campaña, anuncio ni una charla de los 7 días anteriores" },
];

/** Los tramos de la primera respuesta, con el mismo `hastaSeg` que manda el backend (cada uno incluye su borde). */
export const TRAMOS_RESPUESTA_TEXTO: { hastaSeg: number | null; label: string }[] = [
  { hastaSeg: 300, label: "Hasta 5 min" },
  { hastaSeg: 900, label: "De 5 a 15 min" },
  { hastaSeg: 3_600, label: "De 15 min a 1 h" },
  { hastaSeg: 14_400, label: "De 1 a 4 h" },
  { hastaSeg: null, label: "Más de 4 h" },
];

/** Una cantidad que llega del backend: lo que no es un número positivo cuenta 0. */
const cantidad = (x: unknown): number => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export interface FilaDesglose { clave: ClaveDesglose; label: string; detalle: string; color: string; cantidad: number; pct: number | null }

/**
 * Las filas de «Qué pasó con cada consulta». `pct` es la fracción del total (se muestra con
 * `tasaTexto`), null sin consultas: «sin datos» no es «0 %». Las vencidas que el equipo marcó resueltas
 * después siguen vencidas, y el detalle lo dice.
 */
export function filasDesglose(c: ConteoConsultas | null | undefined): FilaDesglose[] {
  const total = cantidad(c?.total);
  const tarde = cantidad(c?.vencidasResueltasTarde);
  return DESGLOSE_CONSULTAS.map((d) => {
    const n = cantidad(c?.[d.clave]);
    const detalle = d.clave === "vencidas" && tarde > 0
      ? `${d.detalle} · ${num(tarde)} de ellas ${tarde === 1 ? "se marcó resuelta" : "se marcaron resueltas"} después (suelen ser «gracias»)`
      : d.detalle;
    return { ...d, detalle, cantidad: n, pct: total > 0 ? n / total : null };
  });
}

/** El divisor de «Respondidas a tiempo»: las que esperaban respuesta y ya terminaron (respondidas + reabiertas + vencidas). */
export function baseATiempo(c: Pick<ConteoConsultas, "respondidas" | "reabiertas" | "vencidas"> | null | undefined): number {
  return cantidad(c?.respondidas) + cantidad(c?.reabiertas) + cantidad(c?.vencidas);
}

/** Las barras de «Cuánto se tardó en contestar»: cantidad y fracción de las medidas, por tramo. */
export function filasTramos(tramos: { hastaSeg: number | null; cantidad: number }[] | null | undefined, n: number | null | undefined):
  { label: string; cantidad: number; pct: number | null }[] {
  const total = cantidad(n);
  return TRAMOS_RESPUESTA_TEXTO.map((t) => {
    const c = cantidad((tramos || []).find((x) => (x?.hastaSeg ?? null) === t.hastaSeg)?.cantidad);
    return { label: t.label, cantidad: c, pct: total > 0 ? c / total : null };
  });
}

/**
 * true si el rango termina antes de que se empezaran a medir las consultas: ahí no hay nada que contar.
 * Con `medidoDesde` null todavía no se midió ninguna, así que todo rango está antes; undefined es que
 * la respuesta no llegó, y eso no es «sin medir».
 */
export function antesDeMedir(medidoDesde: string | null | undefined, rango: RangoFechas): boolean {
  return medidoDesde === null || (esDiaValido(medidoDesde) && rango.hasta < medidoDesde);
}

/** El aviso de un rango que cruza el día en que se empezó a medir; null si el rango está todo antes o todo después. */
export function avisoMedidoDesde(medidoDesde: string | null | undefined, rango: RangoFechas): string | null {
  if (!esDiaValido(medidoDesde) || !(rango.desde < medidoDesde && medidoDesde <= rango.hasta)) return null;
  return `Las consultas se cuentan desde el ${ddmm(medidoDesde)}: antes de ese día no se medían.`;
}

/** Qué decir sin consultas: antes de medir no es «nadie escribió», y sin ninguna medida tampoco. */
export function vacioConsultas(medidoDesde: string | null | undefined, rango: RangoFechas): string {
  if (medidoDesde === null) return TEXTO_SIN_MEDIR;
  return antesDeMedir(medidoDesde, rango) ? `Antes del ${ddmm(String(medidoDesde))} no se medían las consultas.` : TEXTO_SIN_CONSULTAS;
}

export interface FilaGraficoConsultas { fecha: string; etiqueta: string; respondidas: number; sinRespuesta: number; cerradas: number; esperando: number }

/**
 * «Consultas por día» en las cuatro series del gráfico: las cerradas sin responder juntan las
 * resueltas y las bajas; las que no se respondieron a tiempo, las vencidas y las reabiertas.
 */
export function serieConsultasGrafico(serie: FilaDiaConsultas[] | null | undefined): FilaGraficoConsultas[] {
  return (serie || []).filter((f) => !!f?.fecha).map((f) => ({
    fecha: f.fecha,
    etiqueta: ddmm(f.fecha),
    respondidas: cantidad(f.respondidas),
    sinRespuesta: cantidad(f.vencidas) + cantidad(f.reabiertas),
    cerradas: cantidad(f.resueltas) + cantidad(f.bajas),
    esperando: cantidad(f.enCurso),
  }));
}

/** «3 entregadas · 1 fallida · 1 en camino», más «· N sin confirmar» si hay. Sin envíos, vacío. */
export function lineaEnvios(c: ConteoEnviosBandeja | null | undefined): string {
  if (!cantidad(c?.total)) return "";
  const e = cantidad(c?.entregados);
  const f = cantidad(c?.fallidos);
  const s = cantidad(c?.sinConfirmar);
  const partes = [`${num(e)} ${e === 1 ? "entregada" : "entregadas"}`, `${num(f)} ${f === 1 ? "fallida" : "fallidas"}`, `${num(cantidad(c?.enCurso))} en camino`];
  if (s > 0) partes.push(`${num(s)} sin confirmar`);
  return partes.join(" · ");
}
